import { Component, ElementRef, EventEmitter, HostListener, Output, ViewChild, Input, SimpleChanges, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalService } from 'src/app/Services/modal.service';
import { FilterDropdownComponent } from './filter-dropdown/filter-dropdown.component';
import moment from "moment";
import { MatDialog } from '@angular/material/dialog';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { SharedService } from 'src/app/Services/sharedService';
import { TimesheetPresetDateRangeService } from 'src/app/Services/timesheet-preset-date-range.service';
import { DummyDataService } from 'src/app/Services/dummy-data.service';
import { EntitlementsService } from 'src/app/Services/entitlements.service';

@Component({
  selector: 'app-reports-header',
  templateUrl: 'reports-header.component.html',
  styleUrls: ['reports-header.component.css'],
  standalone: false
})
export class ReportsHeaderComponent implements OnInit, OnDestroy {

  @Input('usersList') usersList: any[] = [];
  @Input('teamsList') teamsList: any[] = [];
  @Input('loading') loading: Boolean = false;
  @Input('shifts') shifts: any[] = [];
  /** When false, only the filter controls row is shown (e.g. Productivity Trends has its own page title). */
  @Input() showReportTitle = true;
  /** When set (compact mode), filter button shows this caption instead of the long applied label. */
  @Input() compactButtonCaption: string | null = null;
  /** Tooltip for compact filter button (e.g. full preset + range). */
  @Input() compactButtonTooltip: string | null = null;
  /** When true, compact trigger hides the leading filter icon (text-only control). */
  @Input() compactHideIcon = false;
  /** When true with compact header, the built-in trigger is not rendered (parent supplies its own button). */
  @Input() compactUiHidden = false;
  /** Productivity Trends: expose deep work rules in the filter drawer (outside Advanced). */
  @Input() showDeepWorkSettings = false;
  @Output() filterChanged = new EventEmitter<any>();

  private reportFilterSub!: Subscription;

  typeValue: 'all' | 'app' | 'website' = 'all';
  dateRange: { start: Date | null, end: Date | null };
  selectedShift: any = null;
  selectedEmployees: any[] = [];
  selectedTeam: any = null;
  isFilterDropdownOpen: boolean = false;
  @ViewChild('picker') picker: any;
  @ViewChild('filterAnalysisComponent') filterDropdownComponent!: TemplateRef<any>;
  todayDate: Date = new Date();
  isAppliedFilter: boolean = true;
  currentUserList: any = [];
  currentTeamList: any = [];
  currentFilterData: any = null;
  dialogReferenceAnalysisComponent: any;

  constructor(
    private _modalService: ModalService,
    private dialog: MatDialog,
    private tokenEncryptionService: TokenEncryptionService,
    private _sharedService: SharedService,
    private _timesheetPresetDateRange: TimesheetPresetDateRangeService,
    public _dummyDataService: DummyDataService,
    private entitlementsService: EntitlementsService,
  ) {
    this.dateRange = this.getDefault31DayDateRange();
  }

  /** Matches filter-dropdown default: last 14 days excluding today (ends yesterday). */
  private getDefault31DayDateRange(): { start: Date | null; end: Date | null } {
    const serverDate = this.tokenEncryptionService.getEncryptedItem<any>('user');
    let minDate: Date;
    let maxDate: Date;
    if (serverDate?.meta?.today_date) {
      const vr = this._sharedService.getValidationDateRange(serverDate.meta.today_date, {
        lookbackDays: this.entitlementsService.getLogsRetentionDays(),
      });
      minDate = new Date(vr.minDate);
      maxDate = new Date(vr.maxDate);
    } else {
      const d = new Date();
      minDate = d;
      maxDate = d;
    }
    return this._timesheetPresetDateRange.getPresetRange('last14daysExcludeToday', maxDate, minDate);
  }

  ngOnInit(): void {
    this.entitlementsService.syncFromSession();
    this.syncDefaultDateRangeWithBusinessTimezone();
    this.currentUserList = this.usersList;
    this.currentTeamList = this.teamsList;

    this.reportFilterSub = this._sharedService.reportFilterChanged$.subscribe((filterData: any) => {
      if (!filterData) {
        return;
      }
      this.syncFilterState(filterData);
      // Forward drawer-applied filters to parent pages (e.g. Productivity Trends),
      // which listen to this component's Output to refresh data.
      this.filterChanged.emit(filterData);
    });
  }

  private syncDefaultDateRangeWithBusinessTimezone(): void {
    const user = this.tokenEncryptionService.getEncryptedItem<any>('user');
    const companyId = this.tokenEncryptionService.getEncryptedItem<any>('logedInUser')?.company_id ?? user?.company_id;
    const dateToUse = user?.meta?.today_date || new Date();
    this._sharedService.getValidationDateRangeByCompany(dateToUse, companyId, {
      lookbackDays: this.entitlementsService.getLogsRetentionDays(),
    }).subscribe((range) => {
      this.dateRange = this._timesheetPresetDateRange.getPresetRange('last14daysExcludeToday', range.maxDate, range.minDate);
    });
  }

  ngOnDestroy(): void {
    this.reportFilterSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
    //Add '${implements OnChanges}' to the class.
    if (changes && changes['usersList']?.currentValue) {
      this.currentUserList = changes['usersList']?.currentValue;
    }

    if (changes && changes['teamsList']?.currentValue) {
      this.currentTeamList = changes['teamsList']?.currentValue;
    }

    if (changes && changes['shifts']?.currentValue && this.shifts.length > 0) {
      if (this.selectedShift === undefined) {
        this.selectedShift = null;
      }
    }
  }

  /** Opens the report filter drawer (same as compact date trigger click). */
  openReportFilters(): void {
    this.toggleFilterDropdown();
  }

  toggleFilterDropdown() {
    // ── Restore advanced filter state from currentFilterData ──
    const filterData = this.currentFilterData ? {
      ...this.currentFilterData,
      usersList: this.currentUserList,
      teamsList: this.currentTeamList,
      loading: this.loading,
      shifts: this.shifts,
      // ── Preserve all advanced filters ──
      daySelection: this.currentFilterData.daySelection ?? 'all',
      timeWindow: this.currentFilterData.timeWindow ?? 'fullDay',
      interpretationMode: this.currentFilterData.interpretationMode ?? 'flexible',
      bufferSetting: this.currentFilterData.bufferSetting ?? 'default',
      customDays: this.currentFilterData.customDays ?? { Mon: true, Tue: true, Wed: true, Thu: true, Fri: true },
      startTime: this.currentFilterData.startTime ?? null,
      endTime: this.currentFilterData.endTime ?? null,
      beforeBufferTime: this.currentFilterData.beforeBufferTime ?? 15,
      afterBufferTime: this.currentFilterData.afterBufferTime ?? 15,
      postShiftAnalysisWindowMinutes: this.currentFilterData.postShiftAnalysisWindowMinutes ?? 0,
      deepWorkMinSessionMinutes: this.currentFilterData.deepWorkMinSessionMinutes ?? 90,
      deepWorkMinProductivityPct: this.currentFilterData.deepWorkMinProductivityPct ?? 70,
      deepWorkMaxDistractionPct: this.currentFilterData.deepWorkMaxDistractionPct ?? 20,
      compareWith: this.currentFilterData.compareWith ?? 'previousPeriod',
      showDeepWorkSettings: this.showDeepWorkSettings,
    } : {
      usersList: this.currentUserList,
      teamsList: this.currentTeamList,
      loading: this.loading,
      type: this.typeValue,
      dateRange: this.dateRange,
      selectedShift: this.selectedShift,
      selectedEmployees: this.selectedEmployees,
      selectedTeams: this.selectedTeam,
      shifts: this.shifts,
      daySelection: 'all',
      timeWindow: this.selectedShift ? 'shiftHours' : 'fullDay',
      interpretationMode: 'flexible',
      bufferSetting: 'default',
      customDays: { Mon: true, Tue: true, Wed: true, Thu: true, Fri: true },
      startTime: null,
      endTime: null,
      beforeBufferTime: 15,
      afterBufferTime: 15,
      postShiftAnalysisWindowMinutes: 0,
      deepWorkMinSessionMinutes: 90,
      deepWorkMinProductivityPct: 70,
      deepWorkMaxDistractionPct: 20,
      compareWith: 'previousPeriod',
      showDeepWorkSettings: this.showDeepWorkSettings,
    };

    this._sharedService.componentDrawerSubject.next({
      componentName: "reports-filter",
      componentData: filterData,
      componentAction: "open",
      componentWidth: 450
    });
  }

  openFilterModal() {
    this.dialogReferenceAnalysisComponent = this.dialog.open(this.filterDropdownComponent, {
      panelClass: 'report-filter-modal',
      maxHeight: '80vh',
      disableClose: false,
      data: true
    });
    this.dialogReferenceAnalysisComponent.afterClosed().subscribe((result: any) => {
      console.log("Checking after closed value", result);
    });
  }

  closeFilterDropdown() {
    this.isFilterDropdownOpen = false;
  }

  // @HostListener('document:click', ['$event'])
  // onDocumentClick(event: MouseEvent) {
  //   const target = event.target as HTMLElement;
  //   const filterBtn = document.querySelector('[data-filter-btn]');
  //   const filterDropdown = document.querySelector('[data-filter-dropdown]');

  //   const overlayClick =
  //     target.closest('.cdk-overlay-container');

  //   // If click is inside Angular Material overlay → DO NOTHING
  //   if (overlayClick) {
  //     return;
  //   }

  //   // Close date filter dropdown
  //   if (filterBtn && filterDropdown) {
  //     if (!filterBtn.contains(target) && !filterDropdown.contains(target)) {
  //       this.closeFilterDropdown();
  //     }
  //   }
  // }

  applyFilter(filterData: any) {
    if (filterData) {
      this.syncFilterState(filterData);
      this.filterChanged.emit(filterData);
      this.closeFilterDropdown();
    }
  }

  private syncFilterState(filterData: any) {
    if (!filterData) {
      return;
    }

    // ── Save complete filter state including advanced filters ──
    this.currentFilterData = filterData;
    this.isAppliedFilter = true;

    if (filterData.isReset) {
      this.typeValue = 'all';
      this.dateRange = filterData.dateRange;
      this.selectedShift = filterData.selectedShift;
      this.selectedEmployees = [];
      this.selectedTeam = [];
    } else {
      // ── Store all filter properties for later restoration ──
      this.typeValue = filterData.type;
      this.dateRange = filterData.dateRange;
      this.selectedShift = filterData.selectedShift;
      this.selectedEmployees = filterData.selectedEmployees;
      this.selectedTeam = filterData.selectedTeams;
    }
  }

  get appliedFilterLabel(): string {
    if (!this.isAppliedFilter) {
      return 'Filters';
    }

    const labels: string[] = [];

    // 1. Type
    const typeLabel = this.typeValue === 'all' ? 'All' : (this.typeValue === 'app' ? 'Apps' : 'Websites');
    labels.push(typeLabel);

    // 2. Date Range
    if (this.dateRange.start && this.dateRange.end) {
      const start = moment(this.dateRange.start);
      const end = moment(this.dateRange.end);
      if (start.isSame(end, 'day')) {
        labels.push(start.format('MMM D'));
      } else {
        const isSameYear = start.year() === end.year();
        const startFormat = isSameYear ? 'MMM D' : 'MMM D, YYYY';
        const endFormat = 'MMM D, YYYY';
        labels.push(`${start.format(startFormat)} - ${end.format(endFormat)}`);
      }
    }

    // 3. Shift
    if (this.selectedShift !== null && this.selectedShift !== undefined && this.selectedShift !== '') {
      const shift = this.shifts.find((s: any) => s.id == this.selectedShift);
      if (shift && shift.shift_name && shift.shift_name !== 'All Shifts') {
        labels.push(shift.shift_name);
      }
    }

    // 4. Employees / Teams
    if (this.selectedEmployees && this.selectedEmployees.length > 0) {
      const firstEmpId = this.selectedEmployees[0];
      const firstEmp = this.currentUserList.find((u: any) => u.id == firstEmpId);
      if (firstEmp) {
        let empLabel = firstEmp.name;
        if (this.selectedEmployees.length > 1) {
          empLabel += ` +${this.selectedEmployees.length - 1}`;
        }
        labels.push(empLabel);
      }
    } else if (this.selectedTeam && this.selectedTeam.length > 0) {
      const firstTeamId = this.selectedTeam[0];
      const firstTeam = this.currentTeamList.find((t: any) => t.id == firstTeamId);
      if (firstTeam) {
        labels.push(firstTeam.title.rendered);
      }
    }

    return labels.join(' | ');
  }

  /** Tooltip for compact Productivity Trends filter button. */
  get compactTriggerTooltip(): string {
    const t = (this.compactButtonTooltip ?? '').trim();
    return t.length > 0 ? t : this.appliedFilterLabel;
  }
}
